import { jsPDF } from 'jspdf';

let activePdfUrl = null;
let activePdfUrlCleanup = null;

function formatDate(value) {
  if (!value) return 'Ingen utløpsdato';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('nb-NO', { dateStyle: 'long' }).format(date);
}

function fitText(pdf, value, width, options = {}) {
  const {
    maxFont = 16,
    minFont = 8,
    maxLines = 2,
    color = [24, 49, 83],
    bold = false
  } = options;
  let fontSize = maxFont;
  let lines;

  do {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(fontSize);
    lines = pdf.splitTextToSize(String(value || '—'), width);
    if (lines.length <= maxLines || fontSize <= minFont) break;
    fontSize -= 0.5;
  } while (fontSize >= minFont);

  pdf.setTextColor(...color);
  pdf.setFontSize(fontSize);
  return lines;
}

function drawCenteredText(pdf, value, x, y, width, options = {}) {
  const lines = fitText(pdf, value, width, options);
  pdf.text(lines, x + width / 2, y, { align: 'center', lineHeightFactor: 1.15 });
  return lines.length;
}

function formatVersion(certificate) {
  return [
    certificate.course_version_version,
    certificate.course_version_language_code?.toUpperCase()
  ].filter(Boolean).join(' · ') || '—';
}

function safeFilePart(value) {
  return String(value || 'certificate')
    .replace(/[^\p{L}\p{N}-]+/gu, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'certificate';
}

function validateCertificate(certificate) {
  if (!certificate?.certificate_number) {
    throw new Error('Sertifikatnummer mangler. PDF-en kan ikke genereres.');
  }
  if (!certificate.learner_display_name || !certificate.course_title) {
    throw new Error('Sertifikatdata mangler. PDF-en kan ikke genereres.');
  }
}

function createCertificatePdf(certificate, onDocumentCreated) {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
    compress: true
  });
  onDocumentCreated?.(pdf);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const center = pageWidth / 2;
  const revoked = certificate.status === 'revoked';
  const status = revoked
    ? 'Tilbakekalt — ikke gyldig'
    : certificate.status === 'issued'
      ? 'Utstedt'
      : certificate.status || 'Ukjent';

  pdf.setProperties({
    title: `Kursbevis ${certificate.certificate_number}`,
    subject: certificate.course_title || 'Academy-kursbevis',
    author: certificate.issuer_display_name || 'HMS-innsikt'
  });

  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, 297, 210, 'F');
  pdf.setDrawColor(213, 228, 243);
  pdf.setLineWidth(0.8);
  pdf.rect(12, 12, 273, 186);
  pdf.setDrawColor(237, 243, 249);
  pdf.setLineWidth(0.35);
  pdf.rect(17, 17, 263, 176);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(22, 63, 140);
  pdf.text('HMS-innsikt', 29, 31);

  const statusWidth = Math.min(78, pdf.getTextWidth(status) + 12);
  pdf.setFillColor(...(revoked ? [255, 241, 241] : [237, 248, 241]));
  pdf.setDrawColor(...(revoked ? [233, 180, 184] : [168, 216, 189]));
  pdf.roundedRect(268 - statusWidth, 23, statusWidth, 11, 5, 5, 'FD');
  pdf.setTextColor(...(revoked ? [163, 51, 61] : [23, 101, 59]));
  pdf.setFontSize(8);
  pdf.text(status, 268 - statusWidth / 2, 30, { align: 'center' });

  drawCenteredText(pdf, 'KURSBEVIS', center - 100, 48, 200, {
    maxFont: 8,
    minFont: 8,
    maxLines: 1,
    color: [37, 99, 235],
    bold: true
  });
  drawCenteredText(
    pdf,
    certificate.certificate_display_title || 'Kursbevis',
    31,
    59,
    235,
    { maxFont: 21, minFont: 13, maxLines: 2, color: [22, 63, 140], bold: true }
  );

  drawCenteredText(pdf, 'Dette bekrefter at', 36, 82, 225, {
    maxFont: 10,
    minFont: 10,
    maxLines: 1,
    color: [82, 101, 125]
  });
  drawCenteredText(pdf, certificate.learner_display_name, 32, 94, 233, {
    maxFont: 25,
    minFont: 15,
    maxLines: 1,
    color: [22, 63, 140],
    bold: true
  });
  drawCenteredText(pdf, 'har fullført kurset', 36, 103, 225, {
    maxFont: 9,
    minFont: 9,
    maxLines: 1,
    color: [82, 101, 125]
  });
  drawCenteredText(pdf, certificate.course_title, 32, 115, 233, {
    maxFont: 16,
    minFont: 10,
    maxLines: 2,
    color: [24, 49, 83],
    bold: true
  });
  if (certificate.course_version_title) {
    drawCenteredText(pdf, certificate.course_version_title, 35, 125, 227, {
      maxFont: 8,
      minFont: 7,
      maxLines: 1,
      color: [82, 101, 125]
    });
  }

  const fields = [
    ['Kursversjon', formatVersion(certificate)],
    ['Fullført', formatDate(certificate.completion_completed_at)],
    ['Utstedt', formatDate(certificate.issued_at)],
    ['Gyldig til', certificate.valid_until ? formatDate(certificate.valid_until) : 'Ingen utløpsdato'],
    ['Sertifikatnummer', certificate.certificate_number],
    ['Status', status]
  ];
  const columnWidth = 77;
  const fieldX = [31, 110, 189];
  const rowY = [142, 160];

  pdf.setDrawColor(220, 229, 239);
  pdf.setLineWidth(0.35);
  pdf.line(31, 133, 266, 133);

  fields.forEach(([label, value], index) => {
    const x = fieldX[index % 3];
    const y = rowY[Math.floor(index / 3)];
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.setTextColor(113, 128, 150);
    pdf.text(label.toUpperCase(), x, y);
    const lines = fitText(pdf, value, columnWidth, {
      maxFont: 9,
      minFont: 7,
      maxLines: 2,
      color: revoked && label === 'Status' ? [163, 51, 61] : [24, 49, 83],
      bold: true
    });
    pdf.text(lines, x, y + 5, { lineHeightFactor: 1.1 });
  });

  pdf.setDrawColor(220, 229, 239);
  pdf.line(31, 178, 266, 178);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  pdf.setTextColor(113, 128, 150);
  pdf.text('UTSTEDT AV', 31, 185);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(22, 63, 140);
  pdf.text(certificate.issuer_display_name || 'HMS-innsikt', 31, 191);

  return pdf;
}

export function generateAcademyCertificatePdf(certificate, onDocumentCreated) {
  validateCertificate(certificate);

  let pdf;
  try {
    pdf = createCertificatePdf(certificate, onDocumentCreated);
    const blob = pdf.output('blob');

    if (!(blob instanceof Blob) || blob.type !== 'application/pdf' || blob.size === 0) {
      throw new Error('PDF-filen ble ikke generert korrekt.');
    }

    return blob;
  } catch (error) {
    error.stage = 'generation';
    throw error;
  }
}

function schedulePdfUrlCleanup(url) {
  activePdfUrlCleanup = window.setTimeout(() => {
    URL.revokeObjectURL(url);
    if (activePdfUrl === url) {
      activePdfUrl = null;
      activePdfUrlCleanup = null;
    }
  }, 10 * 60 * 1000);
}

function reportDownloadDiagnostic(callback, event) {
  if (typeof callback !== 'function') return;

  try {
    callback(event);
  } catch {
    // Diagnostics must never interrupt PDF download behavior.
  }
}

export function downloadAcademyCertificatePdf(
  certificate,
  validatedAsset = null,
  onDiagnostic = null
) {
  validateCertificate(certificate);

  if (typeof document === 'undefined' || !document.body) {
    const error = new Error('Nettleseren støtter ikke PDF-nedlasting her.');
    error.stage = 'download';
    reportDownloadDiagnostic(onDiagnostic, {
      stage: 'error',
      message: error.message
    });
    throw error;
  }
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    const error = new Error('Nettleseren støtter ikke PDF-nedlasting her.');
    error.stage = 'download';
    reportDownloadDiagnostic(onDiagnostic, {
      stage: 'error',
      message: error.message
    });
    throw error;
  }

  const reusableAsset = validatedAsset?.blob instanceof Blob
    && validatedAsset.blob.type === 'application/pdf'
    && validatedAsset.blob.size > 0
    && typeof validatedAsset.objectUrl === 'string'
    && validatedAsset.objectUrl.startsWith('blob:')
    ? validatedAsset
    : null;

  let blob;
  try {
    blob = reusableAsset?.blob || generateAcademyCertificatePdf(certificate);
  } catch (error) {
    reportDownloadDiagnostic(onDiagnostic, {
      stage: 'error',
      message: error?.message || 'PDF-Blob kunne ikke genereres.'
    });
    throw error;
  }

  const blobIsValid = blob instanceof Blob
    && blob.type === 'application/pdf'
    && blob.size > 0;
  reportDownloadDiagnostic(onDiagnostic, {
    stage: 'blob-ready',
    valid: blobIsValid,
    size: blob.size,
    type: blob.type
  });

  let objectUrl = reusableAsset?.objectUrl;
  if (!objectUrl) {
    try {
      objectUrl = URL.createObjectURL(blob);
    } catch (error) {
      error.stage = 'download';
      reportDownloadDiagnostic(onDiagnostic, {
        stage: 'url-error',
        message: error?.message || 'Blob-URL kunne ikke opprettes.'
      });
      throw error;
    }
  }

  reportDownloadDiagnostic(onDiagnostic, { stage: 'url-ready' });

  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `Kursbevis-${safeFilePart(certificate.certificate_number)}.pdf`;
  link.style.display = 'none';

  reportDownloadDiagnostic(onDiagnostic, {
    stage: 'link-ready',
    href: link.href,
    download: link.download
  });

  if (!reusableAsset) {
    activePdfUrl = objectUrl;
  }

  try {
    document.body.appendChild(link);
    reportDownloadDiagnostic(onDiagnostic, { stage: 'link-click-started' });
    link.click();
    reportDownloadDiagnostic(onDiagnostic, { stage: 'link-click-returned' });
  } catch (error) {
    error.stage = 'download';
    reportDownloadDiagnostic(onDiagnostic, {
      stage: 'error',
      message: error?.message || 'Nedlastingslenken kunne ikke klikkes.'
    });
    if (!reusableAsset) schedulePdfUrlCleanup(objectUrl);
    throw error;
  } finally {
    link.remove();
  }

  if (!reusableAsset) schedulePdfUrlCleanup(objectUrl);
  return { objectUrl };
}